import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import {
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  CustomInput,
  Button,
  Modal,
  FormFeedback,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { validator, slug, sanitizeDOM, membershipTypes } from '../../utils';
import {
  CREATE_MEMBERSHIP_BY_SLUG,
  UPDATE_MEMBERSHIP_BY_SLUG,
  GQL_GET_USER_CONTENTS,
  GQL_INSERT_USER_CONTENT_FILE,
  GQL_UPDATE_USER_CONTENT_FILE,
  GQL_GET_CONTENT_FILE
} from '../../gqls';
import { updatePaymentLinks } from '../../actions/payment-links';
import MayarAlert from '../alert/alert';
import LoadingAnimation from '../loading-animation';
import Icon from '../icon';
import SubmitButton from '../submit-button';
import RichEditor, { getContentString, getEditorContent, getPlainText } from '../rich-text-editor';
import DatePicker from '../date-time-picker';
import { CustomStyles } from '../custom-styles/react-select-styles';
import Dropzone from '../dropzone';
import MultipleImageVideoInput from '../multiple-image-video-input';

const ModalBodyMembership = ({ onSuccess, isOpen, onClose, data = {} }) => {
  if (!isOpen) return null;

  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only

  const isUpdate = Boolean(data && JSON.stringify(data) !== '{}');

  const dispatch = useDispatch();
  const { register, handleSubmit, errors, control } = useForm();

  const [submitError, setSubmitError] = useState(null);
  const [showMembers, setShowMembers] = useState(Boolean(data.membershipInfo?.showMembers));
  const [isAllowRenewalDiscount, setIsAllowRenewalDiscount] = useState(
    Boolean(data.membershipInfo?.isAllowRenewalDiscount)
  );
  const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(false);
  const [membershipType, setMembershipType] = useState(data.membershipInfo?.type || 'MEMBERSHIP');

  const [multipleImageId, setMultipleImageId] = useState('');
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));

  // Upload file
  const [isDownloadable, setIsDownloadable] = useState(Boolean(data.isDownloadable));
  const [sourceFile, setSourceFile] = useState(data.isDownloadable ? 'existing' : '');
  const [fileId, setFileId] = useState(data.content?.[0]?.file?.id);
  const [notAllowed, setNotAllowed] = useState(false);
  const [fileOptions, setfileOptions] = useState([]);

  const [isAccumulateCredit, setIsAccumulateCredit] = useState(Boolean(data.membershipInfo?.isAccumulateCredit));
  const [isAccumulateTopupCredit, setIsAccumulateTopupCredit] = useState(
    Boolean(data.membershipInfo?.isAccumulateTopupCredit ?? true)
  );

  const [enableCreditTopup, setEnableCreditTopup] = useState(Boolean(data.membershipInfo?.enableCreditTopup));

  const [insertPaymentLinkBySlug, { loading: insertLoading, data: newMembership }] = useMutation(
    CREATE_MEMBERSHIP_BY_SLUG
  );
  const [editPaymentLinkBySlug, { loading: submitUpdateLoading, data: upl }] = useMutation(UPDATE_MEMBERSHIP_BY_SLUG);

  const boolean = [
    { id: 1, label: 'YA', value: true },
    { id: 2, label: 'TIDAK', value: false }
  ];

  const [userUserContent, { loading: insertUserContentLoading, data: userContent }] = useMutation(
    GQL_INSERT_USER_CONTENT_FILE
  );
  const [doUpdateUserContent, { loading: updateUserContentLoading, data: updateUserContent }] = useMutation(
    GQL_UPDATE_USER_CONTENT_FILE
  );

  const { data: getUserContent } = useQuery(GQL_GET_CONTENT_FILE, {
    variables: {
      search: {
        key: [{ operator: 'eq', value: data?.id || 'untitle' }],
        isCoverVideo: [{ operator: 'isEmpty' }]
      }
    },
    fetchPolicy: 'network-only'
  });

  const { loading: loadingFiles, data: dataFiles } = useQuery(GQL_GET_USER_CONTENTS, {
    variables: { limit: 100, offset: 0 },
    context: { v2: true }
  });

  if (dataFiles?.getUserContents?.length && !fileOptions.length) {
    setfileOptions(dataFiles.getUserContents.map(i => ({ value: i.id, label: i.filename })));
  }

  const onSubmit = async values => {
    if (isDescriptionEmpty || (isDownloadable && !fileId)) return;

    values.description = sanitizeDOM(getContentString(values.description));
    delete values.fileSource;

    if (!isUpdate && isDownloadable && !fileId) return;

    if (values.affiliateCommission) values.affiliateCommission = parseInt(values.affiliateCommission);
    else values.affiliateCommission = null;

    const inputVariables = {
      ...values,
      ...(multipleImageId && { multipleImageId }),
      ...(deletedCoverContents.length && {
        deletedCoverContents: deletedCoverContents.map(item => {
          if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
            return {
              videoMultipleFileId: item?.id
            };
          }
          return {
            imageId: item.editImage?.id || item.id
          };
        })
      }),
      ...((isUpdate && multipleCoverContent.length) && {
        orderedMultipleCoverContent: multipleCoverContent.map(item => {
          if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
            return {
              position: Number(item.position),
              videoId: item.videoId || item.file?.id || item.value,
              editId: item?.id
            };
          }
          return {
            position: Number(item.position),
            imageId: item.editImage?.id || item.id
          };
        })
      }),
      ...((!isUpdate && isolatedMultipleCoverContent.length) && {
        orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
          if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
            return {
              position: Number(item.position),
              videoId: item.videoId || item.file?.id || item.value,
              editId: item?.id
            };
          }
          return {
            position: Number(item.position),
            imageId: item.editImage?.id || item.id
          };
        })
      }),
      link: slug(values.name),
      status: 'active',
      type: 'membership',
      hidePortalAccessInEmails: !values.hidePortalAccessInEmails,
      membershipInfo: {
        showMembers: Boolean(showMembers),
        type: membershipType,
        isAllowRenewalDiscount: Boolean(isAllowRenewalDiscount),
        creditValue: parseFloat(values.creditValue),
        enableCreditTopup: Boolean(enableCreditTopup),
        isAccumulateCredit: Boolean(isAccumulateCredit),
        isAccumulateTopupCredit: Boolean(isAccumulateTopupCredit),
        minCreditTopup: enableCreditTopup ? parseFloat(values.minCreditTopup) : null,
        maxCreditTopup: enableCreditTopup ? parseFloat(values.maxCreditTopup) : null
      }
    };
    delete inputVariables.isAllowRenewalDiscount;
    delete inputVariables.creditValue;
    delete inputVariables.enableCreditTopup;
    delete inputVariables.isAccumulateCredit;
    delete inputVariables.isAccumulateTopupCredit;
    delete inputVariables.minCreditTopup;
    delete inputVariables.maxCreditTopup;

    try {
      if (isUpdate) {
        inputVariables.id = data.id;
        inputVariables.membershipInfo.id = data.membershipInfo?.id;

        const editPaymentLink = await editPaymentLinkBySlug({
          variables: { input: inputVariables }
        });

        if (fileId && isDownloadable) {
          const contentId =
            getUserContent.getMultipleFileFilteredList?.[0]?.id || editPaymentLink.data.editPaymentLinkBySlug?.id;

          if (getUserContent.getMultipleFileFilteredList.length > 0) {
            await doUpdateUserContent({
              variables: { input: [{ id: contentId, value: fileId }] }
            });
          } else {
            await userUserContent({
              variables: { input: [{ key: contentId, value: fileId }] }
            });
          }
        }

        if (editPaymentLink.data.editPaymentLinkBySlug?.id) {
          dispatch(updatePaymentLinks(Math.random()));
        }
      } else {
        inputVariables.link = slug(values.name);

        const paymentLink = await insertPaymentLinkBySlug({
          variables: { input: inputVariables }
        });

        if (paymentLink?.data?.insertPaymentLinkBySlug.errorMessage) {
          throw new Error(paymentLink?.data?.insertPaymentLinkBySlug.errorMessage || 'Terjadi kesalahan saat membuat produk');
        }

        if (paymentLink) {
          const paymentLinkId = paymentLink.data.insertPaymentLinkBySlug.id;
          await userUserContent({
            variables: { input: [{ key: paymentLinkId, value: fileId }] }
          });
        }

        window.analytics.track('Create Membership Plan', values);
      }
    } catch (error) {
      setSubmitError(error.message || "Error! Silahkan coba lagi atau hubungi CS");
      window.analytics.track('Create Membership Plan - ERROR', values);
    }
  };

  if (newMembership && newMembership.insertPaymentLinkBySlug && !newMembership.insertPaymentLinkBySlug.errorMessage) onSuccess(newMembership.insertPaymentLinkBySlug.id);

  if (upl) onClose();
  if (upl && (userContent || updateUserContent)) onClose();

  const handleShowMembers = item => setShowMembers(item.value);

  return (
    <Modal isOpen={isOpen} fade size="lg">
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Membership</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>

      <ModalBody>
        <p>
          <small>Terima pembayaran berlangganan dari pelanggan semudah membagikan link</small>
        </p>
        <hr />

        {submitError && <MayarAlert isError message={submitError} />}

        <Form onSubmit={handleSubmit(onSubmit)} id="create-Membership-plan">
          {!isUpdate && (
            <FormGroup>
              <Label for="membershipType">Tipe Produk</Label>
              <Input
                id="membershipType"
                name="membershipType"
                type="select"
                defaultValue={membershipType}
                onChange={e => setMembershipType(e.target.value)}
              >
                {Object.keys(membershipTypes).map(item => (
                  <option key={item} value={item}>
                    {membershipTypes[item]}
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}

          <FormGroup>
            <Label for="name">Nama Paket*</Label>
            <Input
              defaultValue={data.name}
              type="text"
              name="name"
              id="name"
              invalid={Boolean(errors.name)}
              innerRef={register({ required: validator.required })}
            />
            <FormFeedback>{errors.name?.message}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="description">Deskripsi*</Label>
            <Controller
              name="description"
              control={control}
              defaultValue={data.description && getEditorContent(data.description)}
              rules={{ required: { value: true, message: 'Wajib diisi' } }}
              render={({ onChange, value }) => (
                <RichEditor
                  onEditorStateChange={e => {
                    setIsDescriptionEmpty(!getPlainText(e));
                    onChange(e);
                  }}
                  editorState={value}
                  invalidState={Boolean(errors.description || isDescriptionEmpty)}
                />
              )}
            />
            <FormFeedback className={(isDescriptionEmpty || errors.description) && 'd-block'}>Wajib diisi</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="startAt">Waktu Mulai Penjualan</Label>
            <div className="mb-1">
              <Controller
                name="startAt"
                control={control}
                defaultValue={data.startAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih Tanggal Mulai"
                    showTimeSelect
                    dateFormat="yyyy/MM/dd HH:mm"
                    timeFormat="HH:mm"
                    selected={value ? new Date(value) : null}
                    minDate={new Date()}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.startAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.startAt?.message}</FormFeedback>
            <small>
              Kami akan membuka link pembayaran pada tanggal dan waktu yang anda pilih. Opsional, kosongkan untuk
              langsung membuka penjualan
            </small>
          </FormGroup>

          <MultipleImageVideoInput
            productName={data.name}
            setMultipleImageId={setMultipleImageId}
            multipleImage={data.multipleImage}
            onLoading={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isAutoUploadOnSelectFile={false}
            maxVideo={1}
            maxImage={5}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />

          <FormGroup>
            <Label for="showMembers">Tampilkan anggota</Label>
            <Select
              defaultValue={boolean.find(item => item.value === showMembers)}
              name="showMembers"
              invalid={Boolean(errors.showMembers)}
              id="showMembers"
              options={boolean}
              placeholder="Pilih"
              onChange={handleShowMembers}
            ></Select>
            <FormFeedback>{errors.showMembers?.message}</FormFeedback>
          </FormGroup>

          {/^(SAAS|CREDIT)$/.test(membershipType) && (
            <>
              <FormGroup>
                <CustomInput
                  id="isDownloadable"
                  label="Ada file yang didownload setelah Beli?"
                  name="isDownloadable"
                  type="switch"
                  color="primary"
                  defaultChecked={data?.isDownloadable}
                  onChange={e => setIsDownloadable(e.target.checked)}
                  innerRef={register()}
                />
                <small>
                  Jika ada, anda dapat mengupload filenya disini, atau anda bisa menyertakan url file untuk downloadnya
                </small>
              </FormGroup>

              {isDownloadable && (
                <>
                  <FormGroup>
                    <Label for="fileSource">Sumber File*</Label>
                    <Input
                      type="select"
                      id="fileSource"
                      name="fileSource"
                      onChange={e => {
                        setSourceFile(e.target.value);
                      }}
                      invalid={Boolean(errors.fileSource)}
                      innerRef={register({ required: validator.required })}
                      value={sourceFile}
                    >
                      <option value="">-- Pilih Sumber File --</option>
                      {fileOptions.length && <option value="existing">File Lama</option>}
                      <option value="new">Upload Baru</option>
                    </Input>
                    <FormFeedback>{errors.fileSource?.message}</FormFeedback>
                  </FormGroup>

                  {sourceFile === 'existing' && (
                    <FormGroup>
                      <Label for="downloadFileId">Pilih File Lama*</Label>
                      {loadingFiles ? (
                        <span>
                          <LoadingAnimation twodashline />
                        </span>
                      ) : (
                        <Select
                          id="downloadFileId"
                          name="downloadFileId"
                          options={fileOptions}
                          styles={CustomStyles}
                          disabled={loadingFiles}
                          isLoading={loadingFiles}
                          defaultValue={
                            data && data.content
                              ? fileOptions.find(obj => obj.value === data?.content[0]?.file?.id)
                              : null
                          }
                          onChange={e => setFileId(e.value)}
                        />
                      )}
                      {!fileId && <FormFeedback className="error-block">File harus diisi</FormFeedback>}
                    </FormGroup>
                  )}

                  {sourceFile === 'new' && (
                    <FormGroup>
                      <Label for="images">File / Konten*</Label>
                      <Dropzone setFileId={setFileId} maxFiles={1} multiple={false} setNotAllowed={setNotAllowed} />
                      {!fileId && <FormFeedback className="error-block">File harus diisi</FormFeedback>}
                      {notAllowed && (
                        <FormFeedback className="error-block">
                          Nama file tidak boleh menggunakan special character!
                        </FormFeedback>
                      )}
                      <small>Ukuran file maksimal 1GB.</small>
                    </FormGroup>
                  )}
                </>
              )}
            </>
          )}

          {membershipType === 'CREDIT' && (
            <>
              <FormGroup>
                <Label for="creditValue">Harga per 1 credit*</Label>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>Rp</InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="number"
                    name="creditValue"
                    id="creditValue"
                    invalid={errors.creditValue}
                    defaultValue={data?.membershipInfo?.creditValue}
                    innerRef={register({
                      required: validator.required,
                      min: validator.min(1),
                      max: validator.max(100_000_000)
                    })}
                  />
                  <FormFeedback>{errors.creditValue?.message}</FormFeedback>
                </InputGroup>
                <small>Penagihan ini menggunakan mata uang IDR (Rupiah)</small>
              </FormGroup>

              <FormGroup>
                <CustomInput
                  id="isAccumulateCredit"
                  label="Credit rutin/bulanan expired diakhir periode tagihan"
                  name="isAccumulateCredit"
                  type="switch"
                  color="primary"
                  defaultChecked={!data?.membershipInfo?.isAccumulateCredit}
                  onChange={e => setIsAccumulateCredit(!e.target.checked)}
                  innerRef={register()}
                />
              </FormGroup>

              <FormGroup>
                <CustomInput
                  id="enableCreditTopup"
                  label="Member bisa top-up credit dimasa berlangganan"
                  name="enableCreditTopup"
                  type="switch"
                  color="primary"
                  defaultChecked={data?.membershipInfo?.enableCreditTopup}
                  onChange={e => setEnableCreditTopup(e.target.checked)}
                  innerRef={register()}
                />
              </FormGroup>

              {enableCreditTopup && (
                <>
                  <FormGroup>
                    <Label for="minCreditTopup">Minimal Credit Topup*</Label>
                    <Input
                      type="number"
                      name="minCreditTopup"
                      id="minCreditTopup"
                      defaultValue={data?.membershipInfo?.minCreditTopup}
                      invalid={errors.minCreditTopup}
                      innerRef={register({
                        required: validator.required,
                        min: validator.min(1),
                        max: validator.max(1_000_000_000)
                      })}
                    />
                    <FormFeedback>{errors.minCreditTopup?.message}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label for="maxCreditTopup">Maksimal Credit Topup*</Label>
                    <Input
                      type="number"
                      name="maxCreditTopup"
                      id="maxCreditTopup"
                      defaultValue={data?.membershipInfo?.maxCreditTopup}
                      invalid={errors.maxCreditTopup}
                      innerRef={register({
                        required: validator.required,
                        min: validator.min(1),
                        max: validator.max(1_000_000_000)
                      })}
                    />
                    <FormFeedback>{errors.maxCreditTopup?.message}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <CustomInput
                      id="isAccumulateTopupCredit"
                      label="Credit tambahan/top-up tidak pernah expired"
                      name="isAccumulateTopupCredit"
                      type="switch"
                      color="primary"
                      defaultChecked={isAccumulateTopupCredit}
                      onChange={e => setIsAccumulateTopupCredit(e.target.checked)}
                      innerRef={register()}
                    />
                  </FormGroup>
                </>
              )}
            </>
          )}

          <FormGroup>
            <CustomInput
              id="isAllowRenewalDiscount"
              label="Bisa menggunakan diskon saat perpanjangan langganan?"
              name="isAllowRenewalDiscount"
              type="switch"
              color="primary"
              defaultChecked={data?.membershipInfo?.isAllowRenewalDiscount}
              onChange={e => setIsAllowRenewalDiscount(e.target.checked)}
              innerRef={register()}
            />
            <small className="text-muted d-block mt-1">
              Jika diaktifkan, pelanggan dapat menggunakan kode diskon/kupon saat melakukan perpanjangan membership.
            </small>
          </FormGroup>

          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>

                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}

          <FormGroup>
            <CustomInput
              id="hidePortalAccessInEmails"
              name="hidePortalAccessInEmails"
              label="Kirimkan konfirmasi detail membership dan akses portal member ke email pelanggan"
              defaultChecked={!(data?.hidePortalAccessInEmails ?? false)}
              type="switch"
              color="primary"
              innerRef={register()}
            />
          </FormGroup>

          <SubmitButton
            size="lg"
            block
            color="brand"
            type="submit"
            className="text-center"
            loading
            isLoading={insertLoading || submitUpdateLoading || insertUserContentLoading || updateUserContentLoading}
            isDisabled={disableSubmitButton}
            text={`${isUpdate ? 'Edit' : 'Buat'} Membership`}
          />
        </Form>
      </ModalBody>
    </Modal>
  );
};

ModalBodyMembership.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ModalBodyMembership;
